import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InscricaoCatequese } from './inscricao';
import { Pagina } from './pagina';
import {
  PreInscricao,
  PreInscricaoAgenda,
  PreInscricaoAgendaRequest,
  PreInscricaoGrupo,
  PreInscricaoGrupoRequest,
  PreInscricaoInscrito,
  PreInscricaoReenvioConfirmacao,
  PreInscricaoRequest,
  PreInscricaoVaga,
} from './pre-inscricao.dto';

/**
 * Campanhas de pré-inscrição.
 *
 * Três rotas são **públicas** (sem token) e servem a tela de link aberto: `GET .../publico`,
 * `GET .../vaga` e `POST .../inscritos`. As demais exigem sessão e servem a gestão.
 */
@Injectable({ providedIn: 'root' })
export class PreInscricaoService {
  private readonly apiUrl = `${environment.api}/pre-inscricoes`;

  constructor(private http: HttpClient) {}

  // ---------------------------------------------------------------- gestão

  /** `GET /pre-inscricoes` — `CTR-12`: parâmetro vazio não entra na query. */
  listar(page: number, size: number, filtro?: string, sort?: string | null): Observable<Pagina<PreInscricao>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (filtro && filtro.trim() !== '') params = params.set('filtro', filtro.trim());
    if (sort) params = params.set('sort', sort);
    return this.http.get<Pagina<PreInscricao>>(this.apiUrl, { params });
  }

  buscarPorId(id: string): Observable<PreInscricao> {
    return this.http.get<PreInscricao>(`${this.apiUrl}/${encodeURIComponent(id)}`);
  }

  criar(campanha: PreInscricaoRequest): Observable<PreInscricao> {
    return this.http.post<PreInscricao>(this.apiUrl, campanha);
  }

  atualizar(id: string, campanha: PreInscricaoRequest): Observable<PreInscricao> {
    return this.http.put<PreInscricao>(`${this.apiUrl}/${encodeURIComponent(id)}`, campanha);
  }

  /**
   * `DELETE /pre-inscricoes/{id}` — a API **recusa** excluir campanha que já tem inscrito.
   * Tirar do ar é `atualizar` com `ativa: false`.
   */
  excluir(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${encodeURIComponent(id)}`);
  }

  listarGrupos(id: string): Observable<PreInscricaoGrupo[]> {
    return this.http.get<PreInscricaoGrupo[]>(`${this.apiUrl}/${encodeURIComponent(id)}/grupos`);
  }

  /** Faixas de uma mesma campanha não podem se sobrepor: sobreposição volta `400`. */
  criarGrupo(id: string, grupo: PreInscricaoGrupoRequest): Observable<PreInscricaoGrupo> {
    return this.http.post<PreInscricaoGrupo>(`${this.apiUrl}/${encodeURIComponent(id)}/grupos`, grupo);
  }

  /** Reduzir `limiteVagas` abaixo do que o grupo já ocupa volta `400`. */
  atualizarGrupo(id: string, grupoId: string, grupo: PreInscricaoGrupoRequest): Observable<PreInscricaoGrupo> {
    return this.http.put<PreInscricaoGrupo>(
      `${this.apiUrl}/${encodeURIComponent(id)}/grupos/${encodeURIComponent(grupoId)}`,
      grupo,
    );
  }

  /** Grupo com inscrito não pode ser excluído. */
  excluirGrupo(id: string, grupoId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${encodeURIComponent(id)}/grupos/${encodeURIComponent(grupoId)}`,
    );
  }

  /** `GET /pre-inscricoes/{id}/agendas` — em ordem de `dataHora`. */
  listarAgendas(id: string): Observable<PreInscricaoAgenda[]> {
    return this.http.get<PreInscricaoAgenda[]>(`${this.apiUrl}/${encodeURIComponent(id)}/agendas`);
  }

  /** `dataHora` repetida na campanha volta `400`. */
  criarAgenda(id: string, agenda: PreInscricaoAgendaRequest): Observable<PreInscricaoAgenda> {
    return this.http.post<PreInscricaoAgenda>(`${this.apiUrl}/${encodeURIComponent(id)}/agendas`, agenda);
  }

  /**
   * Limite abaixo dos já agendados volta `400`. Mudar `dataHora` remarca quem já estava no
   * horário, **sem** e-mail novo.
   */
  atualizarAgenda(id: string, agendaId: string, agenda: PreInscricaoAgendaRequest): Observable<PreInscricaoAgenda> {
    return this.http.put<PreInscricaoAgenda>(
      `${this.apiUrl}/${encodeURIComponent(id)}/agendas/${encodeURIComponent(agendaId)}`,
      agenda,
    );
  }

  /** Horário com inscrito não pode ser excluído (`400`). */
  excluirAgenda(id: string, agendaId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${encodeURIComponent(id)}/agendas/${encodeURIComponent(agendaId)}`,
    );
  }

  /** `GET /pre-inscricoes/{id}/inscritos` — paginado, mais recentes primeiro. */
  listarInscritos(id: string, page: number, size: number): Observable<Pagina<PreInscricaoInscrito>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<Pagina<PreInscricaoInscrito>>(
      `${this.apiUrl}/${encodeURIComponent(id)}/inscritos`,
      { params },
    );
  }

  /**
   * Reenvia a confirmação da pré-inscrição pelo mesmo caminho do envio automático: todos os
   * endereços da ficha, corpo montado na hora, e cada destinatário vira uma linha em Mensagem.
   */
  reenviarConfirmacao(id: string, inscritoId: string): Observable<PreInscricaoReenvioConfirmacao> {
    return this.http.post<PreInscricaoReenvioConfirmacao>(
      `${this.apiUrl}/${encodeURIComponent(id)}/inscritos/${encodeURIComponent(inscritoId)}/reenviar-confirmacao`,
      {},
    );
  }

  // --------------------------------------------------------------- público

  /** Campanha como o link aberto a vê. Sem token. */
  buscarPublica(id: string): Observable<PreInscricao> {
    return this.http.get<PreInscricao>(`${this.apiUrl}/${encodeURIComponent(id)}/publico`);
  }

  /**
   * Consulta de vaga pela data de nascimento. Sem token, **200 sempre** — inclusive sem
   * vaga. Quem decide é `situacao`; a consulta **não reserva** nada.
   */
  consultarVaga(id: string, dataNascimento: string): Observable<PreInscricaoVaga> {
    const params = new HttpParams().set('dataNascimento', dataNascimento);
    return this.http.get<PreInscricaoVaga>(`${this.apiUrl}/${encodeURIComponent(id)}/vaga`, { params });
  }

  /**
   * Envio da ficha. Sem token, `201` na criação.
   *
   * Mesmo corpo da inscrição em turma, com duas diferenças: `dataNascimento` é
   * **obrigatório** (é ele que define o grupo) e não há `turmaId`. `grupoId` não se manda —
   * o grupo é inferido pela idade.
   *
   * Pode voltar `400` mesmo depois de a consulta ter dito `COM_VAGA`: entre uma coisa e
   * outra a última vaga — ou o último lugar nos horários de atendimento — pode ter sido
   * ocupada. O `201` traz `agendaDataHora`, o atendimento que a família precisa guardar.
   */
  enviarInscrito(id: string, ficha: InscricaoCatequese): Observable<PreInscricaoInscrito> {
    return this.http.post<PreInscricaoInscrito>(`${this.apiUrl}/${encodeURIComponent(id)}/inscritos`, ficha);
  }
}
